import { Component, Emit, Watch, PropSync } from 'vue-property-decorator';
import { AxiosError } from 'axios';
import { Brand } from '@/models/Brand';
import { Supplier } from '@/models/Supplier';
import AbstractFilters from '@/components/CustomFilters/AbstractFilters';
import debounce from 'debounce';
import { cloneDeep } from 'lodash';
import { Category } from '@/models/Category';

@Component<ProductFilters>({})
export default class ProductFilters extends AbstractFilters {
  protected filtered = false;

  protected isLoadingFilters = true;

  protected brands: Brand[] = [];

  protected categories: Category[] = [];

  protected suppliers: Supplier[] = [];

  protected statuses: string[] = ['archived', 'active', 'disabled'];

  protected brandSearch: string = '';
  protected supplierSearch: string = '';
  protected categorySearch: string = '';

  protected showWithoutAttributes = false;
  protected showWithoutVariants = false;

  public searchBrandDebounce: Function = this.handleBrandSearch();
  public searchSupplierDebounce: Function = this.handleSupplierSearch();
  public searchCategoryDebounce: Function = this.handleCategorySearch();

  @PropSync('filters')
  protected productFilter!: ProductFilterItem;

  @PropSync('defaultFilters')
  protected defaultFilter!: ProductFilterItem;

  public async mounted() {
    await this.initialize();
    this.productFilter = this.getProductFilters();
    this.showWithoutAttributes = this.productFilter.has_attributes !== undefined || this.productFilter.has_attributes === '1';
    this.showWithoutVariants = this.productFilter.has_variants !== undefined || this.productFilter.has_variants === '1';

    this.filtered = this.isFiltered;
    this.isLoadingFilters = false;
    this.filtersLoaded();
  }

  protected async initialize() {
    await Promise.all([
      this.getBrands(),
      this.getSuppliers(),
      this.getCategories(),
    ]);
  }

  @Emit()
  protected refreshDataTable() {
    return this.productFilter;
  }

  @Emit()
  protected filtersLoaded() {
    return '';
  }

  @Emit()
  protected filterUpdated() {
    return this.productFilter;
  }

  protected filterChange() {
    this.setProductFilters(this.productFilter);
    this.filterUpdated();

    this.filtered = this.isFiltered;
  }

  protected resetFilter(type: string) {
    this.$set(this.productFilter, type, []);
  }

  protected resetAllFilters() {
    this.$storage.clear();
    this.$set(this.productFilter, 'categories', []);
    this.$set(this.productFilter, 'suppliers', []);
    this.$set(this.productFilter, 'brands', []);
    this.showWithoutAttributes = false;
    this.showWithoutVariants = false;
    this.productFilter = cloneDeep(this.defaultFilter);

    this.filtered = false;
    this.setProductFilters({} as ProductFilterItem);
    this.filterUpdated();
  }

  protected get isFiltered(): boolean {
    const filter = this.productFilter;
    if (
      (filter.brands && filter.brands.length) ||
      (filter.suppliers && filter.suppliers.length)
    ) {
      return true;
    }

    return false;
  }

  protected orderFilters(key:string) {
    if(!(this.productFilter as any)[key]) {
      return;
    }

    const productFilter: string[] = (this.productFilter as any)[key];
    const filter = (this as any)[key];

    let checkedFilterOptions: any[] = filter.filter((filterOption: any) =>{
      if(this.isOptionSelected(productFilter, filterOption.id)) {
        return true;
      }

      if(this.hasChildSelected(productFilter, filterOption)) {
        return true;
      }

      return false;
    });


    let uncheckedFilterOptions: any[] = filter.filter((filterOption: any) =>{
      if(this.isOptionSelected(productFilter, filterOption.id)) {
        return false;
      }

      if(this.hasChildSelected(productFilter, filterOption)) {
        return false;
      }

      return true;
    });


    (this as any)[key] = [...checkedFilterOptions, ...uncheckedFilterOptions];

  }

  private isOptionSelected(selectedOptions: string[], currentOptionId: string): boolean {
    return selectedOptions.includes(currentOptionId);
  }

  private hasChildSelected(selectedOptions: string[], currentOption: any): boolean {
    const children = currentOption.children;
    if(!children || !children.length) {
      return false;
    }

    const childIds = children.map((child: any) => {
      return child.id;
    });

    return childIds.some((id: string) => {
      return selectedOptions.includes(id)
    });
  }

  protected async getBrands(filter: string = '') {
    this.brands = await this.$storage.remember<any[]>(`product_filter_brands_${filter}`, async () => {
      await new Brand()
        .filter('search', filter)
        .limit(100)
        .sort('name', 'ASC')
        .all()
        .then((brands: Brand[]) => {
          this.brands = brands;
        })
        .catch((error: AxiosError) => {});

      return this.brands;
    })
  }

  protected async getSuppliers(filter: string = '') {
    this.suppliers = await this.$storage.remember<any[]>(`product_filter_suppliers_${filter}`, async () => {
      await new Supplier()
        .filter('search', filter)
        .limit(100)
        .sort('name', 'ASC')
        .all()
        .then((suppliers: Supplier[]) => {
          this.suppliers = suppliers;
        })
        .catch((error: AxiosError) => {
        });
      return this.suppliers;
    });
  }

  protected async getCategories(filter: string = "") {
    this.categories = await this.$storage.remember<any[]>(`product_filter_categories_${filter}`, async () => {
      await new Category()
        .limit(100)
        .sort("name", "ASC")
        .all()
        .then((categories: Category[]) => {
          this.categories = categories;

          this.categories.forEach((category: any) => {
            (category as any)["children"] = category.sub_categories;
          });
        })
        .catch((error: AxiosError) => {
        });
      return this.categories;
    });
  }

  protected withoutAttributes() {
    this.productFilter['has_attributes'] = this.showWithoutAttributes
      ? '0'
      : undefined;

    this.filterChange();
  }

  protected withoutVariants() {
    this.productFilter['has_variants'] = this.showWithoutVariants
      ? '0'
      : undefined;

    this.filterChange();
  }

  protected handleCategorySearch() {
    return debounce((searchInput: string) => {
      this.getCategories(searchInput);
    }, 400);
  }

  protected handleBrandSearch() {
    return debounce((searchInput: string) => {
      this.getBrands(searchInput);
    }, 400);
  }

  protected handleSupplierSearch() {
    return debounce((searchInput: string) => {
      this.getSuppliers(searchInput);
    }, 400);
  }

  @Watch("categorySearch")
  protected categorySearchChanged(brand: string) {
    this.searchCategoryDebounce(brand);
  }

  @Watch('brandSearch')
  protected brandSearchChanged(brand: string) {
    this.searchBrandDebounce(brand);
  }

  @Watch('supplierSearch')
  protected supplierSearchChanged(brand: string) {
    this.searchSupplierDebounce(brand);
  }
}


export interface ProductFilterItem {
  suppliers?: string[];
  brands?: string[];
  categories?: string[];
  is_rerun?: boolean;
  has_attributes?: string;
  has_variants?: string;
  status?: string[];
}
